/**
 * Page d'Administration des Modules
 * 
 * Permet de gérer tous les modules de l'application :
 * - Lister les modules existants
 * - Activer/Désactiver les modules
 * - Ajouter des commentaires pour chaque module
 * - Éviter les plantages lors de la désactivation
 */

import React, { useState, useEffect } from 'react';
import { useModuleManager } from '../../services/ModuleManager';
import { useModuleComments } from '../../services/ModuleComments';
import './AdminPanel.css';

const AdminPanel: React.FC = () => {
  const {
    modules,
    moduleStates,
    isModuleActive,
    toggleModule,
    getModuleStats,
    resetAllModules
  } = useModuleManager();
  
  const {
    allComments,
    getCommentCount,
    searchComments,
    getRecentComments,
    getCommentStats,
    exportComments,
    importComments
  } = useModuleComments();

  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [showDependencies, setShowDependencies] = useState<boolean>(false);
  const [showStats, setShowStats] = useState<boolean>(false);

  // Initialiser les modules par défaut si aucun n'est enregistré
  useEffect(() => {
    if (modules.length === 0) {
      // Enregistrer les modules par défaut
      const { moduleManager } = require('../../services/ModuleManager');
      
      const defaultModules = [
        {
          id: 'structure-base',
          name: 'Structure de Base',
          active: true,
          dependencies: [],
          required: true
        },
        {
          id: 'double-poutre',
          name: 'Double Poutre',
          active: true,
          dependencies: ['structure-base']
        },
        {
          id: 'traverses',
          name: 'Traverses',
          active: true,
          dependencies: ['structure-base']
        },
        {
          id: 'soudures',
          name: 'Soudures',
          active: true,
          dependencies: ['structure-base']
        },
        {
          id: 'visualisateur-3d',
          name: 'Visualisateur 3D',
          active: true,
          dependencies: ['structure-base'],
          required: true
        }
      ];
      
      defaultModules.forEach(module => {
        moduleManager.registerModule(module);
      });
    }
  }, [modules.length]);

  // Gérer l'ajout d'un commentaire
  const handleAddComment = async (moduleId: string) => {
    if (newComment.trim()) {
      const { moduleComments } = require('../../services/ModuleComments');
      moduleComments.addComment(moduleId, newComment.trim());
      setNewComment('');
    }
  };

  // Gérer le basculement d'un module
  const handleToggleModule = async (moduleId: string) => {
    try {
      await toggleModule(moduleId);
    } catch (error) {
      console.error('Erreur lors du basculement du module:', error);
      console.error('Erreur lors du changement d\'état du module');
    }
  };

  // Exporter les données
  const handleExportData = () => {
    const data = {
      modules: modules,
      moduleStates: moduleStates,
      comments: exportComments(),
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `module-admin-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Importer les données
  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          if (data.comments) {
            importComments(data.comments);
            console.log('Données importées avec succès!');
          }
        } catch (error) {
          console.error('Erreur lors de l\'importation des données');
        }
      };
      reader.readAsText(file);
    }
  };

  // Filtrer les modules selon les critères
  const filteredModules = modules.filter(module => {
    const matchesSearch = module.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterActive === 'all' || 
                         (filterActive === 'active' && isModuleActive(module.id)) ||
                         (filterActive === 'inactive' && !isModuleActive(module.id));
    
    return matchesSearch && matchesFilter;
  });

  const stats = getModuleStats();
  const commentStats = getCommentStats();

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>🔧 Administration des Modules</h1>
        <p>Gérez l'activation des modules et ajoutez des commentaires</p>
        
        {/* Statistiques */}
        <div className="stats-section">
          <button 
            className="stats-toggle"
            onClick={() => setShowStats(!showStats)}
          >
            📊 {showStats ? 'Masquer' : 'Afficher'} les statistiques
          </button>
          
          {showStats && (
            <div className="stats-grid">
              <div className="stat-card">
                <h4>Modules</h4>
                <p>Total: {stats.total}</p>
                <p>Actifs: {stats.active}</p>
                <p>Inactifs: {stats.inactive}</p>
                <p>Erreurs: {stats.errors}</p>
              </div>
              <div className="stat-card">
                <h4>Commentaires</h4>
                <p>Total: {commentStats.totalComments}</p>
                <p>Modules commentés: {commentStats.modulesWithComments}</p>
                <p>Moyenne: {commentStats.averageCommentsPerModule.toFixed(1)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contrôles */}
      <div className="admin-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Rechercher un module..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterActive === 'all' ? 'active' : ''}`}
            onClick={() => setFilterActive('all')}
          >
            Tous ({modules.length})
          </button>
          <button 
            className={`filter-btn ${filterActive === 'active' ? 'active' : ''}`}
            onClick={() => setFilterActive('active')}
          >
            Actifs ({stats.active})
          </button>
          <button 
            className={`filter-btn ${filterActive === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilterActive('inactive')}
          >
            Inactifs ({stats.inactive})
          </button>
        </div>
        
        <div className="action-buttons">
          <button className="export-btn" onClick={handleExportData}>
            📤 Exporter
          </button>
          <label className="import-btn">
            📥 Importer
            <input 
              type="file" 
              accept=".json" 
              onChange={handleImportData}
              style={{ display: 'none' }}
            />
          </label>
          <button className="reset-btn" onClick={resetAllModules}>
            🔄 Réinitialiser
          </button>
        </div>
      </div>

      {/* Liste des modules */}
      <div className="modules-grid">
        {filteredModules.map(module => {
          const moduleState = moduleStates[module.id];
          const isActive = isModuleActive(module.id);
          const commentCount = getCommentCount(module.id);
          const moduleComments = allComments[module.id] || [];
          
          return (
            <div key={module.id} className={`module-card ${isActive ? 'active' : 'inactive'}`}>
              <div className="module-header">
                <div className="module-info">
                  <h3>{module.name}</h3>
                  <span className={`module-status ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? '🟢 Actif' : '🔴 Inactif'}
                  </span>
                  {module.required && <span className="required-badge">Requis</span>}
                  {moduleState?.error && <span className="error-badge">Erreur</span>}
                </div>
                
                <button
                  className={`toggle-btn ${isActive ? 'active' : 'inactive'}`}
                  onClick={() => handleToggleModule(module.id)}
                  disabled={module.required}
                  title={module.required ? 'Module requis - ne peut pas être désactivé' : 
                         isActive ? 'Désactiver le module' : 'Activer le module'}
                >
                  {isActive ? '🔴 Désactiver' : '🟢 Activer'}
                </button>
              </div>

              <div className="module-content">
                {module.dependencies && module.dependencies.length > 0 && (
                  <div className="module-dependencies">
                    <strong>Dépendances:</strong>
                    <div className="dependencies-list">
                      {module.dependencies.map(dep => (
                        <span key={dep} className="dependency-tag">{dep}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {moduleState?.error && (
                  <div className="module-error">
                    <strong>Erreur:</strong> {moduleState.error}
                  </div>
                )}
              </div>

              {/* Section commentaires */}
              <div className="module-comments">
                <h4>💬 Commentaires ({commentCount})</h4>
                
                {selectedModule === module.id ? (
                  <div className="comment-form">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Ajoutez un commentaire..."
                      className="comment-textarea"
                      rows={3}
                    />
                    <div className="comment-actions">
                      <button 
                        className="save-btn"
                        onClick={() => {
                          handleAddComment(module.id);
                          setSelectedModule(null);
                        }}
                        disabled={!newComment.trim()}
                      >
                        💾 Ajouter
                      </button>
                      <button 
                        className="cancel-btn"
                        onClick={() => {
                          setSelectedModule(null);
                          setNewComment('');
                        }}
                      >
                        ❌ Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="comment-display">
                    {moduleComments.length > 0 ? (
                      <div className="comments-list">
                        {moduleComments.slice(-2).map(comment => (
                          <div key={comment.id} className="comment-item">
                            <p>{comment.text}</p>
                            <small>{new Date(comment.timestamp).toLocaleString()}</small>
                          </div>
                        ))}
                        {moduleComments.length > 2 && (
                          <p className="more-comments">... et {moduleComments.length - 2} autres commentaires</p>
                        )}
                      </div>
                    ) : (
                      <p className="no-comments">Aucun commentaire</p>
                    )}
                    <button 
                      className="add-comment-btn"
                      onClick={() => setSelectedModule(module.id)}
                    >
                      ➕ Ajouter un commentaire
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredModules.length === 0 && (
        <div className="no-modules">
          <p>Aucun module ne correspond aux critères de recherche.</p>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;